/**
 * TerrainSampling — triplanar projection, normal blending, and UV anti-tiling.
 *
 * Provides both CPU mirrors (for tests, baking, and CPU-side preprocessing)
 * and TSL Fn nodes (for GPU material graphs).
 *
 * Techniques:
 *   - Triplanar projection: maps textures along world X, Y, Z axes to eliminate
 *     UV stretching on steep cliffs. Blend weights derived from world normal.
 *     See: https://bgolus.medium.com/normal-mapping-for-a-triplanar-shader-10bf39dca05a
 *
 *   - Whiteout normal blending: correct tangent-space normal composition that
 *     preserves high-frequency detail (Ben Golus / Barré-Brisebois & Hill method).
 *     Unlike RNM, whiteout is cheaper and handles near-flat normals correctly.
 *     See: https://blog.selfshadow.com/publications/blending-in-detail/
 *
 *   - Hex-tiling UV: samples the texture at 3 hex-grid vertices and blends by
 *     barycentric weights, breaking visible repetition at cost of 3x samples.
 *     See: "Histogram-Preserving Blending" — Heitz & Neyret 2018.
 *
 *   - Stochastic UV: per-texel random rotation + offset (Heitz/Neyret 2018).
 *     GPU-only via tslStochasticUV; composed with tslHash21 for the random field.
 */

import {
  Fn, float, vec2, vec3,
  abs, pow, max, normalize,
  fract, floor, sin, cos,
} from 'three/tsl';
import { normalWorld } from 'three/tsl';
import { tslHash21 as _tslHash21 } from './TerrainTSLNoise.js';
 
const tslHash21: any = _tslHash21;

// ─── Shared types ─────────────────────────────────────────────────────────────

/** Simple 3-component vector returned by CPU helpers. */
export interface Vec3 { x: number; y: number; z: number; }

// ─────────────────────────────────────────────────────────────────────────────
// CPU mirrors
// ─────────────────────────────────────────────────────────────────────────────

/**
 * computeTriplanarWeights — triplanar blend weights from world-space normal.
 *
 * Each axis weight is |n_axis|^sharpness, then the three weights are
 * normalised to sum to 1. Higher sharpness values concentrate all weight on
 * the dominant axis, giving a crisper planar boundary. Typical range: 2–8.
 *
 * @param nx        - World-space normal X component.
 * @param ny        - World-space normal Y component.
 * @param nz        - World-space normal Z component.
 * @param sharpness - Power exponent; 1 = linear, 4 = moderate, 8+ = sharp.
 * @returns Object {x, y, z} whose components sum to 1.0.
 */
export function computeTriplanarWeights(
  nx: number,
  ny: number,
  nz: number,
  sharpness: number,
): Vec3 {
  const wx = Math.pow(Math.abs(nx), sharpness);
  const wy = Math.pow(Math.abs(ny), sharpness);
  const wz = Math.pow(Math.abs(nz), sharpness);
  // Guard against a degenerate zero-normal (clamp sum to 1e-5 to avoid /0)
  const sum = Math.max(wx + wy + wz, 1e-5);
  return { x: wx / sum, y: wy / sum, z: wz / sum };
}

// ─── Whiteout normal blending ─────────────────────────────────────────────────

/**
 * whiteoutNormalBlend — Ben Golus whiteout tangent-space normal blend.
 *
 * Formula (Barré-Brisebois & Hill, "Blending in Detail" — SIGGRAPH 2012):
 *   r.xy = base.xy * detail.z + detail.xy * base.z
 *   r.z  = base.z * detail.z
 *   r    = normalize(r)
 *
 * Both base and detail are tangent-space normals (Z-up in tangent space):
 *   n.xy = sample.xy * 2 - 1,  n.z = sample.z  (or sqrt(1 - x² - y²))
 *
 * In tangent space Z is always the up direction regardless of world axis
 * convention — the TSL counterpart below matches this math exactly.
 *
 * @param base   - Base tangent-space normal (e.g. from macro layer).
 * @param detail - Detail tangent-space normal (e.g. from micro layer).
 * @returns Normalised blended tangent-space normal.
 */
export function whiteoutNormalBlend(base: Vec3, detail: Vec3): Vec3 {
  const rx = base.x * detail.z + detail.x * base.z;
  const ry = base.y * detail.z + detail.y * base.z;
  const rz = base.z * detail.z;
  const len = Math.sqrt(rx * rx + ry * ry + rz * rz);
  if (len < 1e-8) return { x: 0, y: 0, z: 1 };
  return { x: rx / len, y: ry / len, z: rz / len };
}

// ─── Hex-tile UV computation ──────────────────────────────────────────────────

/**
 * computeHexTileUVs — compute 3 hex-domain sample UVs with barycentric weights.
 *
 * Maps the UV plane to a hexagonal lattice. For each UV point, the 3 nearest
 * hex vertices are found, random per-vertex rotations and offsets are applied
 * (seeded per-vertex), and barycentric weights relative to the hex triangle
 * give smooth blending. This breaks obvious texture repetition at 3x sample cost.
 *
 * Algorithm (adapted from Heitz & Neyret 2018, "High-Performance By-Example
 * Noise using a Histogram-Preserving Blending Operator"):
 *   1. Transform UV into skewed hex-grid coordinates.
 *   2. Identify the triangle cell and its 3 vertex lattice coords.
 *   3. Compute barycentric weights in the triangle.
 *   4. Per-vertex: hash → random rotation angle + UV offset.
 *   5. Return rotated+offset UV for each vertex, plus normalized weights.
 *
 * @param u    - Input U coordinate (any range).
 * @param v    - Input V coordinate (any range).
 * @param seed - Integer seed for per-vertex hash randomization.
 * @returns { uvs: [u,v][3], weights: number[3] } — weights sum to 1.
 */
export function computeHexTileUVs(
  u: number,
  v: number,
  seed: number,
): { uvs: [number, number][]; weights: number[] } {
  // Step 1 — Transform to skewed hex-grid space.
  // The hex grid uses the oblique coordinate system:
  //   s = u + v * 0.5
  //   t = v * sqrt(3)/2
  const SQRT3_2 = Math.sqrt(3) / 2;
  const s = u + v * 0.5;
  const t = v * SQRT3_2;

  // Integer cell coords
  const si = Math.floor(s);
  const ti = Math.floor(t);

  // Fractional position within cell
  const sf = s - si;
  const tf = t - ti;

  // Step 2 — Triangle selection: upper or lower triangle within the hex cell.
  // The cell is split diagonally: if sf + tf > 1 we're in the upper triangle.
  let v0x: number, v0y: number;
  let v1x: number, v1y: number;
  let v2x: number, v2y: number;
  let w0: number, w1: number, w2: number;

  if (sf + tf > 1.0) {
    // Upper triangle: vertices at (si+1, ti), (si+1, ti+1), (si, ti+1)
    // These are the three cell corners above the sf+tf=1 diagonal.
    v0x = si + 1; v0y = ti;
    v1x = si + 1; v1y = ti + 1;
    v2x = si;     v2y = ti + 1;
    // Barycentric weights for P = V0*w0 + V1*w1 + V2*w2:
    //   X: si+sf = (si+1)*(w0+w1) + si*w2 → w0+w1 = sf
    //   Y: ti+tf = ti*w0 + (ti+1)*(w1+w2) → w1+w2 = tf
    //   w0+w1+w2 = 1 → w0 = 1-tf, w1 = sf+tf-1, w2 = 1-sf
    w0 = 1.0 - tf;
    w1 = sf + tf - 1.0;
    w2 = 1.0 - sf;
  } else {
    // Lower triangle: vertices at (si, ti), (si+1, ti), (si, ti+1)
    // These are the three cell corners below the sf+tf=1 diagonal.
    v0x = si;     v0y = ti;
    v1x = si + 1; v1y = ti;
    v2x = si;     v2y = ti + 1;
    // Barycentric weights for P = V0*w0 + V1*w1 + V2*w2:
    //   X: si+sf = si*(w0+w2) + (si+1)*w1 → w1 = sf
    //   Y: ti+tf = ti*(w0+w1) + (ti+1)*w2 → w2 = tf
    //   w0+w1+w2 = 1 → w0 = 1-sf-tf
    w0 = 1.0 - sf - tf;
    w1 = sf;
    w2 = tf;
  }

  // Step 3 — Normalise weights (they should already sum to 1 but clamp negatives)
  w0 = Math.max(0, w0);
  w1 = Math.max(0, w1);
  w2 = Math.max(0, w2);
  const wsum = w0 + w1 + w2;
  w0 /= wsum;
  w1 /= wsum;
  w2 /= wsum;

  // Step 4 — Per-vertex hash → random rotation + UV offset.
  // We use a simple deterministic hash over (vx, vy, seed).
  function vertexHash(vx: number, vy: number): number {
    // FNV-1a style integer hash mixed with seed
    let h = (vx * 1619 + vy * 31337 + seed * 6971) | 0;
    h ^= h >>> 16;
    h = Math.imul(h, 0x45d9f3b);
    h ^= h >>> 16;
    h = Math.imul(h, 0x45d9f3b);
    h ^= h >>> 16;
    // Normalise to [0, 1)
    return (h >>> 0) / 0x100000000;
  }

  function applyRandomUV(
    inputU: number,
    inputV: number,
    vx: number,
    vy: number,
  ): [number, number] {
    const r0 = vertexHash(vx, vy);
    const r1 = vertexHash(vx + 100, vy + 100);
    const r2 = vertexHash(vx + 200, vy + 200);

    // Random rotation angle in [0, 2π)
    const angle = r0 * Math.PI * 2;
    const cosA = Math.cos(angle);
    const sinA = Math.sin(angle);

    // Random UV offset in [-0.5, 0.5]²
    const offU = r1 - 0.5;
    const offV = r2 - 0.5;

    // Apply rotation about UV origin then add offset
    const ru = cosA * inputU - sinA * inputV + offU;
    const rv = sinA * inputU + cosA * inputV + offV;
    return [ru, rv];
  }

  const uv0 = applyRandomUV(u, v, v0x, v0y);
  const uv1 = applyRandomUV(u, v, v1x, v1y);
  const uv2 = applyRandomUV(u, v, v2x, v2y);

  return {
    uvs:     [uv0, uv1, uv2],
    weights: [w0, w1, w2],
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// TSL Fn nodes (GPU)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * tslTriplanarWeights — TSL node: triplanar blend weights from world normal.
 *
 * Reads the fragment's world-space normal via `normalWorld`, raises each
 * component to `sharpness`, and normalises to produce a vec3 blend weight.
 *
 * @param sharpness - float node: power exponent for axis concentration.
 * @returns vec3 node — blend weights, components sum to 1.
 */
 
export const tslTriplanarWeights: any = /*#__PURE__*/ Fn(([sharpness]: any) => {
  const n  = normalWorld.toVar();
  const wx = pow(abs(n.x), float(sharpness)).toVar();
  const wy = pow(abs(n.y), float(sharpness)).toVar();
  const wz = pow(abs(n.z), float(sharpness)).toVar();
  const total = max(wx.add(wy).add(wz), float(1e-5)).toVar();
  return vec3(wx.div(total), wy.div(total), wz.div(total));
});

/**
 * tslWhiteoutNormalBlend — TSL node: whiteout tangent-space normal blend.
 *
 * Mirrors the CPU `whiteoutNormalBlend` formula exactly on the GPU.
 * Both inputs should be tangent-space normals (decoded from a [0,1] texture
 * as xy = sample.xy * 2 - 1, z = sample.z or sqrt(1 - x² - y²)).
 *
 * @param base   - vec3 node: base tangent-space normal.
 * @param detail - vec3 node: detail tangent-space normal.
 * @returns vec3 node: normalised blended tangent-space normal.
 */
 
export const tslWhiteoutNormalBlend: any = /*#__PURE__*/ Fn(([base, detail]: any) => {
  const b  = vec3(base).toVar();
  const d  = vec3(detail).toVar();
  // Canonical whiteout (Barré-Brisebois & Hill) for tangent-space normals
  // where Z is the up axis. The previous Y-up variant computed
  // `r.y = b.y * d.y`, which collapses toward zero whenever both layers are
  // near-flat (b.y ≈ d.y ≈ 1) — the common terrain case — producing
  // near-zero normals that darken after normalisation. The standard
  // formulation preserves magnitude on flat input and is what the CPU
  // mirror above uses, so CPU-baked normals now match GPU.
  const rx = b.x.mul(d.z).add(d.x.mul(b.z)).toVar();
  const ry = b.y.mul(d.z).add(d.y.mul(b.z)).toVar();
  const rz = b.z.mul(d.z).toVar();
  return normalize(vec3(rx, ry, rz));
});

/**
 * tslStochasticUV — TSL node: Heitz/Neyret stochastic UV randomization.
 *
 * Per-fragment random rotation + offset derived from the integer-lattice cell
 * containing the UV point. Uses tslHash21 as the random function.
 *
 * The cell is determined by floor(uv). For each fragment the hash is:
 *   angle = hash(cell + seed) * 2π
 *   offset = hash22(cell + seed + 1)  [repurposed via two hash21 calls]
 *
 * Then: uvOut = Rot(angle) * uvIn + offset
 *
 * @param uv   - vec2 node: input texture coordinates.
 * @param seed - float node: tile layer seed to distinguish independent layers.
 * @returns vec2 node: stochastically rotated and offset UV.
 */
 
export const tslStochasticUV: any = /*#__PURE__*/ Fn(([uv, seed]: any) => {
  const uvv  = vec2(uv).toVar();
  const s    = float(seed).toVar();

  // Cell lattice coordinate (integer part)
  const cell = floor(uvv).toVar();

  // Random rotation angle in [0, 2π)
  const hashVal = tslHash21(cell.add(s)).toVar();
  const TWO_PI  = float(6.2831853);
  const angle   = hashVal.mul(TWO_PI).toVar();
  const cosA    = cos(angle).toVar();
  const sinA    = sin(angle).toVar();

  // Random UV offset from two independent hash calls
  const cellOff1 = cell.add(vec2(s.add(17.0), s.add(31.0))).toVar();
  const cellOff2 = cell.add(vec2(s.add(53.0), s.add(97.0))).toVar();
  const offU = tslHash21(cellOff1).sub(float(0.5)).toVar();
  const offV = tslHash21(cellOff2).sub(float(0.5)).toVar();

  // Fractional UV within cell (rotate around cell origin, then re-add cell)
  const fUV = fract(uvv).toVar();
  const ru  = cosA.mul(fUV.x).sub(sinA.mul(fUV.y)).add(offU).toVar();
  const rv  = sinA.mul(fUV.x).add(cosA.mul(fUV.y)).add(offV).toVar();

  // Return the per-fragment UV unclamped — the texture sampler's REPEAT
  // wrap mode handles the full real-number domain. The previous
  // `clamp(x, 0, 1e6)` broke every terrain sample with a negative world-X
  // or world-Z (exactly half the terrain) by pinning its UV to the cell
  // edge and smearing the texture along the x=0 / z=0 seam.
  return vec2(cell.x.add(ru), cell.y.add(rv));
});
